import { useEffect, useState } from 'react'
import { ExternalLink, Globe } from 'lucide-react'
import { useI18n } from '../i18n/context'

interface UrlPreviewProps {
  url: string
  compact?: boolean
  featured?: boolean
  metadata?: string[]
}

interface RichUrlPreview {
  url: string
  title?: string
  description?: string
  siteName: string
  imageUrl?: string
  iconUrl?: string
}

function normalizeUrlForOpen(url: string): string {
  const trimmed = url.trim()
  if (/^[a-z][a-z\d+.-]*:/i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function parseUrl(url: string): URL | null {
  try {
    const parsed = new URL(normalizeUrlForOpen(url))
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed : null
  } catch {
    return null
  }
}

function previewFrom(value: unknown): RichUrlPreview | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Record<string, unknown>
  const url = typeof candidate.url === 'string' ? parseUrl(candidate.url)?.href : undefined
  if (!url || typeof candidate.siteName !== 'string' || !candidate.siteName.trim()) return null
  const optionalText = (key: string, maxLength: number) => {
    const item = candidate[key]
    return typeof item === 'string' && item.length <= maxLength && item.trim() ? item.trim() : undefined
  }
  const optionalUrl = (key: string) => {
    const item = candidate[key]
    const parsed = typeof item === 'string' ? parseUrl(item) : null
    return parsed?.protocol === 'https:' ? parsed.href : undefined
  }
  return {
    url,
    siteName: candidate.siteName.trim().slice(0, 100),
    title: optionalText('title', 200),
    description: optionalText('description', 320),
    imageUrl: optionalUrl('imageUrl'),
    iconUrl: optionalUrl('iconUrl'),
  }
}

function useRichUrlPreview(url: string, enabled: boolean): RichUrlPreview | null {
  const [preview, setPreview] = useState<RichUrlPreview | null>(null)

  useEffect(() => {
    setPreview(null)
    if (!enabled) return
    const controller = new AbortController()
    void fetch(`/api/url-preview?url=${encodeURIComponent(url)}`, {
      credentials: 'omit',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return null
      return previewFrom(await response.json())
    }).then((result) => {
      if (result) setPreview(result)
    }).catch(() => undefined)
    return () => controller.abort()
  }, [enabled, url])

  return preview
}

export function UrlPreview({ url, compact = false, featured = false, metadata = [] }: UrlPreviewProps) {
  const [faviconFailed, setFaviconFailed] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)
  const parsed = parseUrl(url)
  const richPreview = useRichUrlPreview(url, Boolean(parsed))
  const { t } = useI18n()
  const host = richPreview?.siteName || parsed?.hostname.replace(/^www\./, '') || url
  const path = parsed ? `${parsed.pathname}${parsed.search}` : ''
  const faviconUrl = richPreview?.iconUrl || ''
  const href = parsed?.href

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset failures when preview assets change
  useEffect(() => {
    setFaviconFailed(false)
    setImageFailed(false)
  }, [faviconUrl, richPreview?.imageUrl])

  const content = (
    <>
      {featured && richPreview?.imageUrl && !imageFailed && (
        <span className="block aspect-[1.91/1] w-full overflow-hidden border-b border-base-300 bg-base-200">
          <img className="h-full w-full object-cover" src={richPreview.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setImageFailed(true)} />
        </span>
      )}
      <span className={featured ? 'grid w-full grid-cols-[3rem_minmax(0,1fr)_auto] items-center gap-3 p-5 max-sm:grid-cols-[3rem_minmax(0,1fr)]' : 'contents'}>
        <span className={`flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-base-300 bg-white text-black ${featured ? 'h-12 w-12' : 'h-10 w-10'}`}>
          {faviconUrl && !faviconFailed ? (
            <img className={`${featured ? 'h-7 w-7' : 'h-6 w-6'} object-contain`} src={faviconUrl} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFaviconFailed(true)} />
          ) : (
            <Globe size={featured ? 28 : 20} aria-hidden="true" />
          )}
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span className={`block font-semibold ${featured ? 'max-h-14 overflow-hidden text-lg' : 'truncate text-sm'}`}>{richPreview?.title || host}</span>
          {richPreview?.description && featured && <span className="mt-1 block max-h-10 overflow-hidden text-sm opacity-70">{richPreview.description}</span>}
          <span className={`block truncate opacity-60 ${featured ? 'mt-2 text-xs' : 'text-xs'}`}>{richPreview ? host : path && path !== '/' ? path : parsed?.protocol.replace(':', '') || url}</span>
          {metadata.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-xs font-medium opacity-60">
              {metadata.map((item) => <span key={item}>{item}</span>)}
            </span>
          )}
        </span>
        {!compact && <span className={`btn btn-ghost btn-xs shrink-0 ${featured ? 'max-sm:col-span-2 max-sm:w-full' : ''}`}><ExternalLink size={14} />{t('default-open')}</span>}
      </span>
    </>
  )

  const className = `group min-w-0 overflow-hidden rounded-lg border border-base-300 bg-base-100 text-base-content no-underline transition-colors ${featured ? 'flex flex-col shadow-xl' : 'flex items-center gap-3'} ${compact ? 'mt-3 p-3' : `w-full hover:border-neutral hover:bg-base-200 ${featured ? '' : 'mx-auto max-w-sm p-3'}`}`

  if (compact || !href) {
    return <div className={className}>{content}</div>
  }

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </a>
  )
}
